'use client'

import type { ComponentProps, DragEvent, ReactNode } from 'react'

import { useState } from 'react'
import { useToast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { avatarPhoto } from '@/constants/avatars'

import {
    BuildingsIcon,
    CaretDownIcon,
    CaretRightIcon,
    CheckCircleIcon,
    ClockIcon,
    GrainsIcon,
    GrowIcon,
    HammerIcon,
    HeartIcon,
    LeafIcon,
    NewspaperIcon,
    PawPrintIcon,
    PlusIcon,
    RocketIcon,
    XIcon
} from '@/components/icons/icon-set'

import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuLabel,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

/*
 * GoodUnited / Kanban blocks. Figma: 23 / Blocks / Kanban and 32 / Templates / Kanban.
 * Board -> KanbanToolbar + KanbanStages + KanbanSwimlane (KanbanOrganizationHeader + KanbanColumns -> KanbanColumn -> KanbanCard).
 * Planning spans columns 1–2, Building spans 3–4. Columns keep task order and grow with their content.
 * Cards drag between the four columns of their own organization; the board confirms each move with a toast.
 */

export type KanbanStatus = 'awaiting-review' | 'approved' | 'building' | 'launched'
export type KanbanCategory = 'content' | 'grow'

export const KANBAN_STATUSES: { id: KanbanStatus, label: string, icon: ReactNode }[] = [
    { id: 'awaiting-review', label: 'Awaiting review', icon: <ClockIcon /> },
    { id: 'approved', label: 'Approved', icon: <CheckCircleIcon /> },
    { id: 'building', label: 'Building in progress', icon: <HammerIcon /> },
    { id: 'launched', label: 'Launched', icon: <RocketIcon /> }
]

const categories: Record<KanbanCategory, { label: string, icon: ReactNode }> = {
    content: { label: 'Content', icon: <NewspaperIcon /> },
    grow: { label: 'Grow', icon: <GrowIcon /> }
}

export interface KanbanTask {
    id: string
    title: string
    category: KanbanCategory
    status: KanbanStatus
    assignee: string
}

export interface KanbanOrganization {
    id: string
    name: string
    logo: ReactNode
    tasks: KanbanTask[]
}

interface KanbanCardProps extends Omit<ComponentProps<'button'>, 'children'> {
    task: Pick<KanbanTask, 'title' | 'category' | 'assignee'>
    selected?: boolean
}

/* Kanban/Card: title, category badge and assignee. Hover lifts to Colors/surface/subtle; selected uses Colors/border/selected. */
export function KanbanCard({ task, selected = false, className, draggable, onDragStart, onDragEnd, ...props }: KanbanCardProps) {
    const category = categories[task.category]
    const [dragging, setDragging] = useState(false)

    return (
        <button
            type='button'
            data-slot='kanban-card'
            data-selected={selected || undefined}
            data-dragging={dragging || undefined}
            aria-pressed={selected}
            draggable={draggable}
            onDragStart={event => {
                setDragging(true)
                onDragStart?.(event)
            }}
            onDragEnd={event => {
                setDragging(false)
                onDragEnd?.(event)
            }}
            className={cn(
                'flex w-full cursor-pointer flex-col gap-3 data-dragging:opacity-50 rounded-lg border border-border-subtle bg-surface-raised p-3 text-left transition-colors outline-none hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus data-selected:border-border-selected data-selected:hover:bg-surface-raised',
                className
            )}
            {...props}
        >
            <span className='text-sm font-medium text-text-primary'>
                {task.title}
            </span>
            <Separator />
            <span className='flex items-center justify-between gap-2'>
                <Badge className='border border-border-subtle px-1.5 text-xs [&_svg]:size-3.5'>
                    {category.icon}
                    {category.label}
                </Badge>
                <Avatar size='sm' aria-label={`Assigned to ${task.assignee}`}>
                    <AvatarImage src={avatarPhoto(task.assignee)} alt='' />
                    <AvatarFallback>
                        {task.assignee.split(' ').map(part => part[0]).join('')}
                    </AvatarFallback>
                </Avatar>
            </span>
        </button>
    )
}

/* Kanban/Stages: two phases aligned with the four columns. */
export function KanbanStages({ className }: { className?: string }) {
    return (
        <div data-slot='kanban-stages' className={cn('grid grid-cols-4 gap-4', className)}>
            <div className='col-span-2 rounded-md bg-surface-muted px-3 py-2.5 text-sm text-text-secondary'>
                Planning
            </div>
            <div className='col-span-2 rounded-md bg-surface-muted px-3 py-2.5 text-sm text-text-secondary'>
                Building
            </div>
        </div>
    )
}

interface KanbanColumnProps extends Omit<ComponentProps<'div'>, 'children'> {
    status: KanbanStatus
    count: number
    children?: ReactNode
    onDropTask?: (taskId: string) => void
}

const TASK_TYPE = 'application/x-goodunited-task'

/* Kanban/Column: a lane with its status icon, label and count, and a Cards slot. */
export function KanbanColumn({ status, count, className, children, onDropTask, ...props }: KanbanColumnProps) {
    const config = KANBAN_STATUSES.find(item => item.id === status) ?? KANBAN_STATUSES[0]
    const [over, setOver] = useState(false)

    const dropProps = onDropTask
        ? {
            onDragOver: (event: DragEvent<HTMLDivElement>) => {
                if (!event.dataTransfer.types.includes(TASK_TYPE)) return
                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
                setOver(true)
            },
            onDragLeave: (event: DragEvent<HTMLDivElement>) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver(false)
            },
            onDrop: (event: DragEvent<HTMLDivElement>) => {
                event.preventDefault()
                setOver(false)
                const taskId = event.dataTransfer.getData(TASK_TYPE)
                if (taskId) onDropTask(taskId)
            }
        }
        : {}

    return (
        <div
            data-slot='kanban-column'
            role='group'
            aria-label={`${config.label}, ${count}`}
            data-over={over || undefined}
            className={cn('flex min-h-24 flex-col gap-2 self-stretch rounded-lg bg-surface-muted p-2 outline-2 outline-offset-0 outline-transparent transition-colors data-over:bg-brand-subtle data-over:outline-border-selected data-over:outline-dashed', className)}
            {...dropProps}
            {...props}
        >
            <div className='flex h-7 items-center gap-2 px-1 text-sm text-text-primary'>
                <span className='inline-flex text-icon-secondary [&_svg]:size-4' aria-hidden='true'>
                    {config.icon}
                </span>
                <span className='min-w-0 flex-1 truncate font-medium'>
                    {config.label}
                </span>
                <span className='text-text-secondary tabular-nums'>
                    {count}
                </span>
            </div>
            {children}
        </div>
    )
}

interface KanbanColumnsProps {
    tasks: KanbanTask[]
    selected?: string | null
    onSelect?: (task: KanbanTask) => void
    onMove?: (taskId: string, status: KanbanStatus) => void
    className?: string
}

/* Kanban/Columns: the four shared lanes. With onMove, cards drag between them. */
export function KanbanColumns({ tasks, selected, onSelect, onMove, className }: KanbanColumnsProps) {
    return (
        <div data-slot='kanban-columns' className={cn('grid grid-cols-4 items-start gap-4', className)}>
            {KANBAN_STATUSES.map(status => {
                const items = tasks.filter(task => task.status === status.id)

                return (
                    <KanbanColumn
                        key={status.id}
                        status={status.id}
                        count={items.length}
                        onDropTask={onMove ? taskId => {
                            const task = tasks.find(item => item.id === taskId)
                            if (task && task.status !== status.id) onMove(taskId, status.id)
                        } : undefined}
                    >
                        {items.map(task => (
                            <KanbanCard
                                key={task.id}
                                task={task}
                                selected={selected === task.id}
                                onClick={() => onSelect?.(task)}
                                draggable={!!onMove}
                                onDragStart={event => {
                                    event.dataTransfer.setData(TASK_TYPE, task.id)
                                    event.dataTransfer.effectAllowed = 'move'
                                }}
                                aria-roledescription={onMove ? 'Draggable card' : undefined}
                            />
                        ))}
                    </KanbanColumn>
                )
            })}
        </div>
    )
}

interface KanbanOrganizationHeaderProps {
    name: string
    logo?: ReactNode
    count: number
    open: boolean
    onToggle: () => void
    controls?: string
}

/* Kanban/OrganizationHeader: disclosure, logo (Avatar), name and total count. */
export function KanbanOrganizationHeader({ name, logo, count, open, onToggle, controls }: KanbanOrganizationHeaderProps) {
    return (
        <button
            type='button'
            data-slot='kanban-organization-header'
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={controls}
            className='flex h-12 w-full cursor-pointer items-center gap-3 rounded-lg border border-border-subtle bg-surface-raised px-3 text-left transition-colors outline-none hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus'
        >
            {open
                ? <CaretDownIcon className='size-4 shrink-0 text-icon-secondary' aria-hidden='true' />
                : <CaretRightIcon className='size-4 shrink-0 text-icon-secondary' aria-hidden='true' />}
            <Avatar size='sm' className='bg-surface-subtle text-icon-secondary [&_svg]:size-3.5'>
                <AvatarFallback>
                    {logo ?? <BuildingsIcon aria-hidden='true' />}
                </AvatarFallback>
            </Avatar>
            <span className='min-w-0 truncate text-sm font-medium text-text-primary'>
                {name}
            </span>
            <Badge className='px-1.5 py-0 text-xs'>
                {count}
            </Badge>
        </button>
    )
}

interface KanbanSwimlaneProps {
    organization: KanbanOrganization
    defaultOpen?: boolean
    selected?: string | null
    onSelect?: (task: KanbanTask) => void
    onMove?: (taskId: string, status: KanbanStatus) => void
}

/* Kanban/Swimlane: one organization, collapsed or expanded. */
export function KanbanSwimlane({ organization, defaultOpen = false, selected, onSelect, onMove }: KanbanSwimlaneProps) {
    const [open, setOpen] = useState(defaultOpen)
    const panelId = `swimlane-${organization.id}`

    return (
        <section data-slot='kanban-swimlane' aria-label={organization.name} className='flex flex-col gap-2'>
            <KanbanOrganizationHeader
                name={organization.name}
                logo={organization.logo}
                count={organization.tasks.length}
                open={open}
                onToggle={() => setOpen(!open)}
                controls={panelId}
            />
            {open && <KanbanColumns tasks={organization.tasks} selected={selected} onSelect={onSelect} onMove={onMove} className='pb-2' />}
        </section>
    )
}

interface KanbanToolbarProps {
    filters: string[]
    options?: string[]
    onFiltersChange: (filters: string[]) => void
    onAddAction?: () => void
    className?: string
}

/* Kanban/Toolbar: applied filters, Clear all, Filter and Add action. */
export function KanbanToolbar({ filters, options = ['Content', 'Grow', 'Red Cross', 'Feeding America'], onFiltersChange, onAddAction, className }: KanbanToolbarProps) {
    const toggle = (value: string) => onFiltersChange(filters.includes(value) ? filters.filter(item => item !== value) : [...filters, value])

    return (
        <div data-slot='kanban-toolbar' className={cn('flex flex-wrap items-center justify-between gap-3', className)}>
            <div className='flex flex-wrap items-center gap-2'>
                {filters.map(filter => (
                    <Button key={filter} variant='outline' size='sm' onClick={() => toggle(filter)} aria-label={`Remove filter ${filter}`}>
                        {filter}
                        <XIcon aria-hidden='true' />
                    </Button>
                ))}
                {filters.length > 0 && (
                    <Button variant='ghost' size='sm' onClick={() => onFiltersChange([])}>
                        {`Clear all (${filters.length})`}
                    </Button>
                )}
            </div>
            <div className='flex items-center gap-2'>
                <DropdownMenu>
                    <DropdownMenuTrigger render={<Button variant='outline' size='sm' />}>
                        Filter
                        <CaretDownIcon aria-hidden='true' />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                        <DropdownMenuGroup>
                            <DropdownMenuLabel>
                                Filter actions
                            </DropdownMenuLabel>
                            {options.map(option => (
                                <DropdownMenuCheckboxItem key={option} checked={filters.includes(option)} onCheckedChange={() => toggle(option)}>
                                    {option}
                                </DropdownMenuCheckboxItem>
                            ))}
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
                <Button size='sm' onClick={onAddAction}>
                    Add action
                    <PlusIcon aria-hidden='true' />
                </Button>
            </div>
        </div>
    )
}

const task = (id: string, title: string, category: KanbanCategory, status: KanbanStatus, assignee: string): KanbanTask => ({ id, title, category, status, assignee })

export const INBOX_ORGANIZATIONS: KanbanOrganization[] = [
    {
        id: 'american-heart-association',
        name: 'American Heart Association',
        logo: <HeartIcon className='text-chart-negative' />,
        tasks: [
            task('aha-1', 'Create social media content', 'content', 'awaiting-review', 'Adelina Hoxha'),
            task('aha-2', 'Create social media content', 'content', 'awaiting-review', 'Adelina Hoxha'),
            task('aha-3', 'Create social media content', 'content', 'awaiting-review', 'Adelina Hoxha'),
            task('aha-4', 'Create social media content', 'content', 'approved', 'Adelina Hoxha'),
            task('aha-5', 'Host a monthly book club', 'grow', 'building', 'Marco Silva')
        ]
    },
    {
        id: 'best-friends-animal-society',
        name: 'Best Friends Animal Society',
        logo: <PawPrintIcon className='text-chart-challenges' />,
        tasks: [
            task('bfas-1', 'Launch an adoption challenge', 'grow', 'awaiting-review', 'Marco Silva'),
            task('bfas-2', 'Write shelter success stories', 'content', 'approved', 'Adelina Hoxha'),
            task('bfas-3', 'Create social media content', 'content', 'launched', 'Adelina Hoxha')
        ]
    },
    {
        id: 'environmental-defense-fund',
        name: 'Environmental Defense Fund',
        logo: <LeafIcon className='text-chart-platform' />,
        tasks: [
            task('edf-1', 'Plan an Earth Day fundraiser', 'grow', 'awaiting-review', 'Marco Silva')
        ]
    },
    {
        id: 'feeding-america',
        name: 'Feeding America',
        logo: <GrainsIcon className='text-chart-instagram' />,
        tasks: [
            task('fa-1', 'Create social media content', 'content', 'building', 'Adelina Hoxha')
        ]
    },
    {
        id: 'sebas-foundation-stage',
        name: 'Sebas foundation stage',
        logo: <HeartIcon className='text-chart-misc' />,
        tasks: [
            task('sfs-1', 'Create social media content', 'content', 'awaiting-review', 'Adelina Hoxha'),
            task('sfs-2', 'Create social media content', 'content', 'awaiting-review', 'Adelina Hoxha'),
            task('sfs-3', 'Host a monthly book club', 'grow', 'approved', 'Marco Silva'),
            task('sfs-4', 'Create social media content', 'content', 'approved', 'Adelina Hoxha'),
            task('sfs-5', 'Run a birthday fundraiser push', 'grow', 'building', 'Marco Silva'),
            task('sfs-6', 'Create social media content', 'content', 'launched', 'Adelina Hoxha')
        ]
    }
]

interface KanbanBoardProps {
    organizations?: KanbanOrganization[]
    defaultFilters?: string[]
    defaultOpen?: string[]
    onSelectTask?: (task: KanbanTask) => void
    onAddAction?: () => void
    className?: string
}

/* Content/Kanban/Inbox: the toolbar, the stages and one swimlane per organization. Scrolls sideways on small screens. */
export function KanbanBoard({
    organizations = INBOX_ORGANIZATIONS,
    defaultFilters = ['Grow', 'Red Cross'],
    defaultOpen = [organizations[0]?.id ?? ''],
    onSelectTask,
    onAddAction,
    className
}: KanbanBoardProps) {
    const [filters, setFilters] = useState(defaultFilters)
    const [selected, setSelected] = useState<string | null>(null)
    const [boards, setBoards] = useState(organizations)
    const { toast } = useToast()

    // Move a card to another status column and confirm the change.
    const move = (taskId: string, status: KanbanStatus) => {
        const organization = boards.find(item => item.tasks.some(entry => entry.id === taskId))
        const moved = organization?.tasks.find(entry => entry.id === taskId)
        const column = KANBAN_STATUSES.find(item => item.id === status)
        if (!organization || !moved || !column || moved.status === status) return

        setBoards(current => current.map(item => item.id !== organization.id
            ? item
            : { ...item, tasks: [...item.tasks.filter(entry => entry.id !== taskId), { ...moved, status }] }))

        toast({
            title: status === 'approved' ? 'Action approved' : `Moved to ${column.label}`,
            description: status === 'approved'
                ? `"${moved.title}" for ${organization.name} is now approved.`
                : `"${moved.title}" for ${organization.name} is now ${column.label.toLowerCase()}.`
        })
    }

    const select = (item: KanbanTask) => {
        setSelected(current => current === item.id ? null : item.id)
        onSelectTask?.(item)
    }

    return (
        <div data-slot='kanban-board' className={cn('flex flex-col gap-4', className)}>
            <KanbanToolbar filters={filters} onFiltersChange={setFilters} onAddAction={onAddAction} />
            <div className='no-scrollbar -mx-1 overflow-x-auto px-1 pb-1'>
                <div className='flex min-w-220 flex-col gap-3'>
                    <KanbanStages />
                    {boards.map(organization => (
                        <KanbanSwimlane
                            key={organization.id}
                            organization={organization}
                            defaultOpen={defaultOpen.includes(organization.id)}
                            selected={selected}
                            onSelect={select}
                            onMove={move}
                        />
                    ))}
                </div>
            </div>
        </div>
    )
}

/* Content/Kanban/Empty: the starter for a new organization. */
export function KanbanEmptyBoard({ onAddAction, className }: { onAddAction?: () => void, className?: string }) {
    return (
        <KanbanBoard
            organizations={[{ id: 'organization', name: 'Organization name', logo: null, tasks: [] }]}
            defaultFilters={[]}
            defaultOpen={['organization']}
            onAddAction={onAddAction}
            className={className}
        />
    )
}
